import { useState } from 'react'
import { Button, Hinweisdialog } from './primitives'
import { merkeGefragt, schaltePushEin } from '@/features/updates/push'

/**
 * Die einmalige Frage nach Benachrichtigungen.
 *
 * **Warum nicht gleich die Systemabfrage?** Weil sie nur einmal gestellt werden kann.
 * Wer sie ungefragt vorgesetzt bekommt, wischt sie weg — und danach lässt sie sich
 * ausschließlich in den Einstellungen des Geräts zurückholen, wo niemand sucht. Dieser
 * Dialog erklärt erst, worum es geht; die Systemabfrage folgt nur auf ein bewusstes Ja.
 * Ein Nein kostet dagegen nichts: Es verbraucht den einen Versuch nicht, und der
 * Schalter unter „Mehr" bleibt offen.
 */
export function PushPrompt({ onErledigt }: { onErledigt: () => void }) {
  const [läuft, setLäuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const einschalten = async () => {
    setLäuft(true)
    setFehler(null)
    try {
      // Der Ausgang wird hier nicht kommentiert. „an" ist der Normalfall; wer in der
      // Systemabfrage ablehnt, hat das bewusst getan und braucht keine Meldung
      // darüber. Den Zustand nennt jederzeit der Abschnitt unter „Mehr".
      await schaltePushEin()
      await merkeGefragt()
      onErledigt()
    } catch (problem) {
      setFehler((problem as Error).message)
      setLäuft(false)
    }
  }

  const ablehnen = async () => {
    await merkeGefragt()
    onErledigt()
  }

  return (
    // Escape wirkt wie „Nein, danke": Wer den Dialog wegschiebt, hat entschieden — und
    // die Frage soll ihn kein zweites Mal aufhalten.
    <Hinweisdialog titel="Über neue Karten informiert werden?" onAbbruch={() => void ablehnen()}>
      <p className="text-sm text-ink">
        <strong className="font-semibold">Über neue Karten informiert werden?</strong>{' '}
        Die App meldet sich dann, sobald Taschenkarten hinzukommen oder überarbeitet
        werden — auch wenn sie geschlossen ist.
      </p>

      <div className="mt-3 flex items-center gap-3">
        <Button variant="primary" onClick={() => void einschalten()} disabled={läuft}>
          {läuft ? 'Einen Moment …' : 'Ja, informieren'}
        </Button>
        <button
          type="button"
          onClick={() => void ablehnen()}
          className="tap -mr-2 shrink-0 px-2 text-sm text-muted hover:text-ink"
        >
          Nein, danke
        </button>
      </div>

      {fehler && <p className="mt-2 text-sm text-muted">{fehler}</p>}

      <p className="mt-3 text-xs leading-relaxed text-muted">
        Lässt sich jederzeit unter „Mehr" ändern.
      </p>
    </Hinweisdialog>
  )
}
